import { DetectedCharacter } from './invisibleCharacters';
import { CONFUSABLES, isConfusable, getLatinEquivalent } from './confusables';
import { isPUACharacter, getPUACategory, isTagBlockCharacter } from './invisibleCharacters';

export interface HomoglyphMatch {
  position: number;
  character: string;
  lookalike: string;
  script: string;
  confidence: number;
  description: string;
}

export interface ScriptMixing {
  scripts: string[];
  positions: { script: string; start: number; end: number; char: string }[];
  isSuspicious: boolean;
  mixedWords: { word: string; position: number; scripts: string[] }[];
}

export interface NormalizationResult {
  isUnstable: boolean;
  originalLength: number;
  nfcLength: number;
  nfdLength: number;
  nfkcLength: number;
  nfkdLength: number;
  changesDetected: string[];
}

export interface RepeatingPattern {
  isRepeating: boolean;
  interval: number;
  confidence: number;
  positions: number[];
  description: string;
}

export interface PositionalPattern {
  startClustering: boolean;
  endClustering: boolean;
  wordBoundaryOnly: boolean;
  lineEndingOnly: boolean;
  distribution: 'start' | 'end' | 'boundaries' | 'uniform' | 'clustered';
  clusterPositions: number[];
}

export interface DiacriticsAbuse {
  hasAbuse: boolean;
  affectedPositions: { position: number; baseChar: string; diacriticCount: number }[];
  maxDiacriticsOnChar: number;
}

export interface FormatAbuse {
  hasAbuse: boolean;
  bomCount: number;
  softHyphenCount: number;
  separatorCount: number;
  positions: { type: string; position: number; char: string }[];
}

export interface SemanticContextIssue {
  type: 'word_boundary_violation' | 'sentence_structure_disruption';
  affectedWords: { word: string; position: number; splitPositions: number[] }[];
  description: string;
}

export interface URLConfusable {
  url: string;
  position: number;
  issues: { type: 'punycode' | 'tld_spoof' | 'subdomain_trick'; details: string }[];
  decodedUrl?: string;
}

export interface BiDiExploit {
  type: 'rlo_lro_override' | 'trojan_source' | 'mixed_direction';
  positions: number[];
  displayedText: string;
  actualText: string;
  risk: 'HIGH' | 'CRITICAL';
}

export interface ZeroWidthChain {
  pattern: string; // e.g., "ZWSP+ZWJ+ZWNJ"
  positions: number[];
  chainLength: number;
  isStackedInvisible: boolean;
}

export interface LanguageSpecificIssue {
  language: string;
  issueType: 'turkish_i' | 'german_sharp_s' | 'vietnamese_tone' | 'arabic_form';
  positions: number[];
  description: string;
}

export interface MathSymbolSubstitution {
  positions: { position: number; substitute: string; original: string }[];
  category: 'bold' | 'italic' | 'script' | 'fraktur' | 'monospace' | 'enclosed';
  count: number;
}

export interface ControlCharExploit {
  type: 'ansi_escape' | 'backspace_delete' | 'form_feed' | 'terminal_control';
  sequences: { position: number; sequence: string; description: string }[];
  risk: 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

// ===== PUA & 2024 UNICODE THREAT INTERFACES =====

export interface PUADetection {
  hasPUA: boolean;
  totalPUACount: number;
  density: number; // PUA characters / total characters
  ranges: { range: string; count: number; percentage: number }[];
  patterns: { type: string; likelihood: 'low' | 'medium' | 'high' }[];
  positions: number[];
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  suggestedCategory: 'icon_font' | 'cjk_gaiji' | 'steganography' | 'unknown';
}

export interface FullwidthExploitation {
  hasFullwidth: boolean;
  dangerousChars: { position: number; char: string; code: string; normalizesTo: string }[];
  cveRelated: 'CVE-2024-4577' | 'CVE-2024-34078' | 'none';
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

export interface TagBlockAnomalies {
  hasTagBlockChars: boolean;
  count: number;
  positions: number[];
  inEmojiContext: boolean;
  suspiciousPatterns: boolean;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

export interface EmojiTokenAnalysis {
  hasComplexEmoji: boolean;
  emojiSequences: { position: number; sequence: string; componentCount: number; estimatedTokens: number }[];
  tokenExpansionRisk: boolean;
  estimatedTokenCount: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

/**
 * Unicode script ranges for detecting script mixing
 */
const SCRIPT_RANGES = [
  { name: 'Latin', start: 0x0020, end: 0x024F },
  { name: 'Latin-Extended', start: 0x1E00, end: 0x1EFF },
  { name: 'Cyrillic', start: 0x0400, end: 0x04FF },
  { name: 'Greek', start: 0x0370, end: 0x03FF },
  { name: 'Arabic', start: 0x0600, end: 0x06FF },
  { name: 'Hebrew', start: 0x0590, end: 0x05FF },
  { name: 'CJK', start: 0x4E00, end: 0x9FFF },
  { name: 'Hiragana', start: 0x3040, end: 0x309F },
  { name: 'Katakana', start: 0x30A0, end: 0x30FF },
  { name: 'Thai', start: 0x0E00, end: 0x0E7F },
  { name: 'Bengali', start: 0x0980, end: 0x09FF },
  { name: 'Cherokee', start: 0x13A0, end: 0x13FF },
];

/**
 * Get the Unicode script of a character
 */
function getCharacterScript(charCode: number): string {
  for (const range of SCRIPT_RANGES) {
    if (charCode >= range.start && charCode <= range.end) {
      return range.name;
    }
  }
  return 'Other';
}

/**
 * Detect homoglyph/confusable characters in text
 * Only flags cross-script confusables (e.g., Cyrillic 'А' vs Latin 'A')
 * Ignores same-script confusables (e.g., Latin 'l' vs Latin '1')
 */
export function detectHomoglyphs(text: string): HomoglyphMatch[] {
  const matches: HomoglyphMatch[] = [];
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    
    if (isConfusable(char)) {
      const latinEquiv = getLatinEquivalent(char);
      if (latinEquiv) {
        // Find which confusable it is
        const confusables = CONFUSABLES[latinEquiv] || [];
        const confusable = confusables.find(c => c.char === char);
        
        if (confusable) {
          // IMPORTANT: Only flag if it's a different script (cross-script confusable)
          // Ignore same-script confusables like Latin 'l' vs Latin '1'
          if (confusable.script === 'Latin') {
            continue; // Skip Latin-to-Latin confusables
          }
          
          // Calculate confidence based on context
          let confidence = 80; // Base confidence
          
          // Higher confidence if surrounded by Latin characters
          const prevChar = i > 0 ? text[i - 1] : '';
          const nextChar = i < text.length - 1 ? text[i + 1] : '';
          const prevIsLatin = /[a-zA-Z]/.test(prevChar);
          const nextIsLatin = /[a-zA-Z]/.test(nextChar);
          
          if (prevIsLatin && nextIsLatin) {
            confidence = 95;
          } else if (prevIsLatin || nextIsLatin) {
            confidence = 90;
          }
          
          matches.push({
            position: i,
            character: char,
            lookalike: latinEquiv,
            script: confusable.script,
            confidence,
            description: `${confusable.desc} looks like Latin '${latinEquiv}'`,
          });
        }
      }
    }
  }
  
  return matches;
}

/**
 * Analyze script mixing in text
 */
export function analyzeScriptMixing(text: string): ScriptMixing {
  const positions: { script: string; start: number; end: number; char: string }[] = [];
  const scriptsSet = new Set<string>();
  const mixedWords: { word: string; position: number; scripts: string[] }[] = [];
  
  // Track scripts for each character
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const code = char.charCodeAt(0);
    const script = getCharacterScript(code);
    
    if (script !== 'Other') {
      scriptsSet.add(script);
      positions.push({ script, start: i, end: i, char });
    }
  }
  
  const scripts = Array.from(scriptsSet);
  
  // Analyze words for mixed scripts
  const words = text.split(/\s+/);
  let currentPos = 0;
  
  for (const word of words) {
    const wordPos = text.indexOf(word, currentPos);
    if (wordPos === -1) continue;
    
    const wordScripts = new Set<string>();
    for (let i = 0; i < word.length; i++) {
      const code = word.charCodeAt(i);
      const script = getCharacterScript(code);
      if (script !== 'Other') {
        wordScripts.add(script);
      }
    }
    
    // Flag if word mixes multiple scripts (excluding Latin-Extended which is normal)
    const wordScriptsArray = Array.from(wordScripts).filter(s => s !== 'Latin-Extended');
    if (wordScriptsArray.length > 1) {
      mixedWords.push({
        word,
        position: wordPos,
        scripts: wordScriptsArray,
      });
    }
    
    currentPos = wordPos + word.length;
  }
  
  // Determine if mixing is suspicious
  const isSuspicious = mixedWords.length > 0 || 
    (scripts.length > 1 && scripts.includes('Cyrillic') && scripts.includes('Latin'));
  
  return {
    scripts,
    positions,
    isSuspicious,
    mixedWords,
  };
}

/**
 * Check for Unicode normalization instability
 */
export function checkNormalizationInstability(text: string): NormalizationResult {
  const nfc = text.normalize('NFC');
  const nfd = text.normalize('NFD');
  const nfkc = text.normalize('NFKC');
  const nfkd = text.normalize('NFKD');
  
  const changesDetected: string[] = [];
  
  if (text !== nfc && text.length !== nfc.length) {
    changesDetected.push('NFC normalization changes length');
  }
  if (text !== nfd && text.length !== nfd.length) {
    changesDetected.push('NFD normalization changes length');
  }
  if (text !== nfkc && text.length !== nfkc.length) {
    changesDetected.push('NFKC normalization changes length');
  }
  if (text !== nfkd && text.length !== nfkd.length) {
    changesDetected.push('NFKD normalization changes length');
  }
  
  // Check for specific problematic characters
  if (text.includes('ﬁ') || text.includes('ﬂ') || text.includes('ﬀ')) {
    changesDetected.push('Contains ligatures that decompose during normalization');
  }
  
  return {
    isUnstable: changesDetected.length > 0,
    originalLength: text.length,
    nfcLength: nfc.length,
    nfdLength: nfd.length,
    nfkcLength: nfkc.length,
    nfkdLength: nfkd.length,
    changesDetected,
  };
}

/**
 * Detect repeating invisible character patterns
 */
export function detectRepeatingInvisiblePattern(
  _text: string,
  detectedChars: DetectedCharacter[]
): RepeatingPattern | null {
  if (detectedChars.length < 3) {
    return null;
  }
  
  const positions = detectedChars.map(d => d.position).sort((a, b) => a - b);
  
  // Calculate intervals between consecutive invisible characters
  const intervals: number[] = [];
  for (let i = 1; i < positions.length; i++) {
    intervals.push(positions[i] - positions[i - 1]);
  }
  
  // Find most common interval
  const intervalCounts = new Map<number, number>();
  intervals.forEach(interval => {
    intervalCounts.set(interval, (intervalCounts.get(interval) || 0) + 1);
  });
  
  // Find the most frequent interval
  let maxCount = 0;
  let commonInterval = 0;
  intervalCounts.forEach((count, interval) => {
    if (count > maxCount) {
      maxCount = count;
      commonInterval = interval;
    }
  });
  
  // Ignore trivial consecutive patterns (interval = 1) unless there's variety
  // Consecutive characters are normal and not indicative of steganography
  if (commonInterval === 1 && intervalCounts.size === 1) {
    // All characters are consecutive, not a meaningful pattern
    return null;
  }
  
  // Also ignore interval=1 if it dominates but we want to detect actual encoding patterns
  // Only report interval=1 if there's significant variation (indicating mixed patterns)
  if (commonInterval === 1 && (maxCount / intervals.length) > 0.8) {
    // More than 80% are consecutive, likely just normal text
    return null;
  }
  
  // Check if there's a repeating pattern (at least 40% of intervals match)
  const repeatPercentage = (maxCount / intervals.length) * 100;
  
  if (repeatPercentage >= 40 && maxCount >= 3 && commonInterval >= 2) {
    const confidence = Math.min(95, Math.round(repeatPercentage));
    
    return {
      isRepeating: true,
      interval: commonInterval,
      confidence,
      positions,
      description: `Invisible characters repeat every ~${commonInterval} positions (${maxCount}/${intervals.length} intervals match)`,
    };
  }
  
  return null;
}

/**
 * Analyze positional patterns of invisible characters
 */
export function analyzePositionalPatterns(
  detectedChars: DetectedCharacter[],
  textLength: number
): PositionalPattern | null {
  if (detectedChars.length === 0 || textLength === 0) {
    return null;
  }
  
  const positions = detectedChars.map(d => d.position);
  const startThreshold = Math.ceil(textLength * 0.05); // First 5%
  const endThreshold = Math.floor(textLength * 0.95); // Last 5%
  
  let startCount = 0;
  let endCount = 0;
  
  positions.forEach(pos => {
    if (pos < startThreshold) startCount++;
    if (pos > endThreshold) endCount++;
  });
  
  const startPercentage = (startCount / detectedChars.length) * 100;
  const endPercentage = (endCount / detectedChars.length) * 100;
  
  const startClustering = startPercentage >= 60;
  const endClustering = endPercentage >= 60;
  
  // Determine distribution type
  let distribution: 'start' | 'end' | 'boundaries' | 'uniform' | 'clustered' = 'uniform';
  
  if (startClustering) {
    distribution = 'start';
  } else if (endClustering) {
    distribution = 'end';
  } else if (startPercentage + endPercentage >= 70) {
    distribution = 'boundaries';
  } else {
    // Check for clustering
    const avgPosition = positions.reduce((a, b) => a + b, 0) / positions.length;
    const variance = positions.reduce((sum, pos) => sum + Math.pow(pos - avgPosition, 2), 0) / positions.length;
    const stdDev = Math.sqrt(variance);
    
    if (stdDev < textLength * 0.1) {
      distribution = 'clustered';
    }
  }
  
  return {
    startClustering,
    endClustering,
    wordBoundaryOnly: false, // Would need word boundary detection
    lineEndingOnly: false, // Would need line ending detection
    distribution,
    clusterPositions: distribution === 'clustered' ? positions : [],
  };
}

/**
 * Detect combining diacritics abuse
 */
export function detectDiacriticsAbuse(text: string): DiacriticsAbuse {
  const affectedPositions: { position: number; baseChar: string; diacriticCount: number }[] = [];
  let maxDiacriticsOnChar = 0;
  
  // Combining diacritical marks range: U+0300 to U+036F
  const combiningMarksRange = { start: 0x0300, end: 0x036F };
  
  let i = 0;
  while (i < text.length) {
    const baseChar = text[i];
    const baseCode = baseChar.charCodeAt(0);
    
    // Skip if this is itself a combining mark
    if (baseCode >= combiningMarksRange.start && baseCode <= combiningMarksRange.end) {
      i++;
      continue;
    }
    
    // Count following combining marks
    let diacriticCount = 0;
    let j = i + 1;
    
    while (j < text.length) {
      const code = text.charCodeAt(j);
      if (code >= combiningMarksRange.start && code <= combiningMarksRange.end) {
        diacriticCount++;
        j++;
      } else {
        break;
      }
    }
    
    if (diacriticCount > 3) {
      affectedPositions.push({
        position: i,
        baseChar,
        diacriticCount,
      });
      maxDiacriticsOnChar = Math.max(maxDiacriticsOnChar, diacriticCount);
    }
    
    i = j || i + 1;
  }
  
  return {
    hasAbuse: affectedPositions.length > 0,
    affectedPositions,
    maxDiacriticsOnChar,
  };
}

/**
 * Detect format character abuse
 */
export function detectFormatCharacterAbuse(text: string): FormatAbuse {
  const positions: { type: string; position: number; char: string }[] = [];
  let bomCount = 0;
  let softHyphenCount = 0;
  let separatorCount = 0;
  
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const char = text[i];
    
    // BOM (Byte Order Mark) - should only be at start
    if (code === 0xFEFF) {
      if (i > 0) {
        positions.push({ type: 'BOM', position: i, char });
        bomCount++;
      }
    }
    
    // Soft Hyphen
    else if (code === 0x00AD) {
      positions.push({ type: 'Soft Hyphen', position: i, char });
      softHyphenCount++;
    }
    
    // Line/Paragraph Separators
    else if (code === 0x2028 || code === 0x2029) {
      positions.push({ 
        type: code === 0x2028 ? 'Line Separator' : 'Paragraph Separator', 
        position: i, 
        char 
      });
      separatorCount++;
    }
  }
  
  return {
    hasAbuse: positions.length > 0,
    bomCount,
    softHyphenCount,
    separatorCount,
    positions,
  };
}

/**
 * Analyze semantic context for invisible characters breaking words
 */
export function analyzeSemanticContext(
  text: string,
  detectedChars: DetectedCharacter[]
): SemanticContextIssue | null {
  const affectedWords: { word: string; position: number; splitPositions: number[] }[] = [];
  
  // Common evasion keywords to check
  const evasionPatterns = [
    /pass\S*word/gi,
    /vi\S*agra/gi,
    /p\S*rn/gi,
    /c\S*sino/gi,
    /l\S*ttery/gi,
    /admin/gi,
    /hack/gi,
  ];
  
  // Build a position map of invisible characters
  const invisiblePositions = new Set(detectedChars.map(d => d.position));
  
  // Split text into words and analyze each
  const words = text.split(/\s+/);
  let currentPos = 0;
  
  for (const word of words) {
    const wordPos = text.indexOf(word, currentPos);
    if (wordPos === -1) continue;
    
    // Check if this word contains invisible characters
    const splitPositions: number[] = [];
    for (let i = wordPos; i < wordPos + word.length; i++) {
      if (invisiblePositions.has(i)) {
        splitPositions.push(i);
      }
    }
    
    if (splitPositions.length > 0) {
      // Check if it matches any evasion pattern (after removing invisible chars)
      const cleanWord = word.replace(/[\u200B-\u200D\uFEFF]/g, '');
      const matchesEvasionPattern = evasionPatterns.some(pattern => pattern.test(cleanWord));
      
      if (matchesEvasionPattern || word.length > 5) {
        affectedWords.push({
          word,
          position: wordPos,
          splitPositions,
        });
      }
    }
    
    currentPos = wordPos + word.length;
  }
  
  if (affectedWords.length === 0) {
    return null;
  }
  
  return {
    type: 'word_boundary_violation',
    affectedWords,
    description: `${affectedWords.length} word${affectedWords.length > 1 ? 's' : ''} contain embedded invisible characters, potentially for filter evasion`,
  };
}

/**
 * Detect URL/domain confusables including punycode and TLD spoofing
 */
export function detectURLConfusables(text: string): URLConfusable[] {
  const urlPattern = /(https?:\/\/)?([\w-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?/gi;
  const confusables: URLConfusable[] = [];
  
  let match;
  while ((match = urlPattern.exec(text)) !== null) {
    const url = match[0];
    const position = match.index;
    const issues: { type: 'punycode' | 'tld_spoof' | 'subdomain_trick'; details: string }[] = [];
    
    // Check for punycode (IDN homograph attack)
    if (url.includes('xn--')) {
      const punycodeMatch = url.match(/xn--[a-z0-9-]+/gi);
      if (punycodeMatch) {
        issues.push({
          type: 'punycode',
          details: `Punycode detected: ${punycodeMatch.join(', ')}. This may be used for domain spoofing.`,
        });
      }
    }
    
    // Check for TLD spoofing (Cyrillic/Greek TLDs that look like Latin)
    const tldMatch = url.match(/\.([a-zA-Z]{2,})$/);
    if (tldMatch) {
      const tld = tldMatch[1];
      // Check if TLD contains non-Latin characters that look Latin
      for (let i = 0; i < tld.length; i++) {
        const char = tld[i];
        const code = char.charCodeAt(0);
        // Check for Cyrillic 'о' (0x043E) instead of Latin 'o', etc.
        if (code >= 0x0400 && code <= 0x04FF) {
          issues.push({
            type: 'tld_spoof',
            details: `TLD contains Cyrillic characters that look like Latin. Possible spoofing of .com, .org, etc.`,
          });
          break;
        }
      }
    }
    
    // Check for subdomain tricks (user@domain pattern)
    if (url.includes('@')) {
      const parts = url.split('@');
      if (parts.length === 2) {
        issues.push({
          type: 'subdomain_trick',
          details: `URL contains '@' character. The actual domain is '${parts[1]}', not '${parts[0]}'.`,
        });
      }
    }
    
    if (issues.length > 0) {
      confusables.push({
        url,
        position,
        issues,
        decodedUrl: undefined,
      });
    }
  }
  
  return confusables;
}

/**
 * Detect bidirectional text exploits (RLO/LRO attacks)
 */
export function detectBiDiExploits(text: string): BiDiExploit | null {
  const bidiChars = [
    { code: 0x202D, name: 'LRO', type: 'rlo_lro_override' },
    { code: 0x202E, name: 'RLO', type: 'rlo_lro_override' },
    { code: 0x202A, name: 'LRE', type: 'trojan_source' },
    { code: 0x202B, name: 'RLE', type: 'trojan_source' },
  ];
  
  const positions: number[] = [];
  let exploitType: 'rlo_lro_override' | 'trojan_source' | 'mixed_direction' = 'rlo_lro_override';
  
  // Detect BiDi override characters
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const bidiChar = bidiChars.find(b => b.code === code);
    if (bidiChar) {
      positions.push(i);
      exploitType = bidiChar.type as 'rlo_lro_override' | 'trojan_source';
    }
  }
  
  if (positions.length === 0) {
    // Check for mixed direction (RTL + LTR)
    const hasRTL = /[\u0590-\u05FF\u0600-\u06FF]/.test(text); // Hebrew/Arabic
    const hasLTR = /[a-zA-Z]/.test(text);
    
    if (hasRTL && hasLTR) {
      // Count direction changes
      let directionChanges = 0;
      let lastWasRTL = false;
      
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const isRTL = /[\u0590-\u05FF\u0600-\u06FF]/.test(char);
        const isLTR = /[a-zA-Z]/.test(char);
        
        if ((isRTL && !lastWasRTL) || (isLTR && lastWasRTL)) {
          directionChanges++;
          if (isRTL || isLTR) {
            positions.push(i);
          }
        }
        
        if (isRTL) lastWasRTL = true;
        if (isLTR) lastWasRTL = false;
      }
      
      // Excessive direction changes are suspicious
      if (directionChanges > 5) {
        return {
          type: 'mixed_direction',
          positions,
          displayedText: text.substring(0, 50) + '...',
          actualText: text.substring(0, 50) + '...',
          risk: 'HIGH',
        };
      }
    }
    
    return null;
  }
  
  // RLO/LRO is CRITICAL, especially in filenames
  const risk: 'HIGH' | 'CRITICAL' = exploitType === 'rlo_lro_override' ? 'CRITICAL' : 'HIGH';
  
  // Simulate what the text would look like (simplified - real rendering is complex)
  const displayedText = text.substring(0, 50) + (text.length > 50 ? '...' : '');
  
  return {
    type: exploitType,
    positions,
    displayedText,
    actualText: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
    risk,
  };
}

/**
 * Detect zero-width character chains
 */
export function detectZeroWidthChains(
  text: string,
   
  _detectedChars: DetectedCharacter[]
): ZeroWidthChain | null {
  const zwChars = [
    { code: 0x200B, name: 'ZWSP' },
    { code: 0x200C, name: 'ZWNJ' },
    { code: 0x200D, name: 'ZWJ' },
    { code: 0xFEFF, name: 'ZWNBSP' },
  ];
  
  const chains: { pattern: string; positions: number[]; length: number }[] = [];
  let currentChain: string[] = [];
  let currentPositions: number[] = [];
  
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const zw = zwChars.find(z => z.code === code);
    
    if (zw) {
      currentChain.push(zw.name);
      currentPositions.push(i);
    } else {
      // Chain ended
      if (currentChain.length >= 2) {
        chains.push({
          pattern: currentChain.join('+'),
          positions: [...currentPositions],
          length: currentChain.length,
        });
      }
      currentChain = [];
      currentPositions = [];
    }
  }
  
  // Check last chain
  if (currentChain.length >= 2) {
    chains.push({
      pattern: currentChain.join('+'),
      positions: [...currentPositions],
      length: currentChain.length,
    });
  }
  
  if (chains.length === 0) {
    return null;
  }
  
  // Check for stacked invisibles (same position)
  const isStacked = chains.some(chain => {
    const uniquePositions = new Set(chain.positions);
    return uniquePositions.size < chain.positions.length;
  });
  
  // Get the longest/most complex chain
  const longestChain = chains.reduce((a, b) => a.length > b.length ? a : b);
  
  return {
    pattern: longestChain.pattern,
    positions: longestChain.positions,
    chainLength: longestChain.length,
    isStackedInvisible: isStacked,
  };
}

/**
 * Detect language-specific attacks (Turkish İ, German ß, etc.)
 */
export function detectLanguageSpecificAttacks(text: string): LanguageSpecificIssue[] {
  const issues: LanguageSpecificIssue[] = [];
  
  // 1. Turkish İ/I detection
  const turkishIPositions: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    // Turkish: İ (0x0130), ı (0x0131)
    if (char === 'İ' || char === 'ı') {
      // Check context - if surrounded by Latin, likely an attack
      const prevChar = i > 0 ? text[i - 1] : '';
      const nextChar = i < text.length - 1 ? text[i + 1] : '';
      if (/[a-zA-Z]/.test(prevChar) || /[a-zA-Z]/.test(nextChar)) {
        turkishIPositions.push(i);
      }
    }
  }
  
  if (turkishIPositions.length > 0) {
    issues.push({
      language: 'Turkish',
      issueType: 'turkish_i',
      positions: turkishIPositions,
      description: `Turkish İ/ı characters in Latin context. These have different case-folding rules and can bypass filters.`,
    });
  }
  
  // 2. German ß (sharp S) detection
  const germanSSPositions: number[] = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === 'ß') {
      germanSSPositions.push(i);
    }
  }
  
  if (germanSSPositions.length > 0) {
    // Check if it's in an unexpected context (e.g., email/username)
    const hasEmail = /@/.test(text);
    if (hasEmail) {
      issues.push({
        language: 'German',
        issueType: 'german_sharp_s',
        positions: germanSSPositions,
        description: `German ß in potentially case-sensitive context. Normalizes to 'ss' but may cause validation issues.`,
      });
    }
  }
  
  // 3. Vietnamese/Thai tone marks in wrong order
  const toneMarkPositions: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    // Vietnamese tone marks: U+0300-U+0323
    // Thai tone marks: U+0E48-U+0E4B
    if ((code >= 0x0300 && code <= 0x0323) || (code >= 0x0E48 && code <= 0x0E4B)) {
      // Check if it's not preceded by a valid base character
      if (i === 0 || text.charCodeAt(i - 1) < 0x0041) {
        toneMarkPositions.push(i);
      }
    }
  }
  
  if (toneMarkPositions.length > 0) {
    issues.push({
      language: 'Vietnamese/Thai',
      issueType: 'vietnamese_tone',
      positions: toneMarkPositions,
      description: `Tone marks in invalid positions. May cause rendering issues or normalization problems.`,
    });
  }
  
  // 4. Arabic forms (initial/medial/final) out of context
  const arabicFormPositions: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    // Arabic Presentation Forms-A: U+FB50-U+FDFF
    // Arabic Presentation Forms-B: U+FE70-U+FEFF
    if ((code >= 0xFB50 && code <= 0xFDFF) || (code >= 0xFE70 && code <= 0xFEFF)) {
      // Check if surrounded by non-Arabic
      const prevCode = i > 0 ? text.charCodeAt(i - 1) : 0;
      const nextCode = i < text.length - 1 ? text.charCodeAt(i + 1) : 0;
      const arabicRange = (c: number) => (c >= 0x0600 && c <= 0x06FF);
      
      if (!arabicRange(prevCode) && !arabicRange(nextCode)) {
        arabicFormPositions.push(i);
      }
    }
  }
  
  if (arabicFormPositions.length > 0) {
    issues.push({
      language: 'Arabic',
      issueType: 'arabic_form',
      positions: arabicFormPositions,
      description: `Arabic presentation forms used out of context. These should only appear in connected Arabic text.`,
    });
  }
  
  return issues;
}

/**
 * Detect mathematical/symbol substitution
 */
export function detectMathSymbolSubstitution(text: string): MathSymbolSubstitution[] {
  const substitutions: MathSymbolSubstitution[] = [];
  
  // Math Alphanumeric Symbols ranges
  const mathRanges = [
    { start: 0x1D400, end: 0x1D433, category: 'bold', base: 0x0041 }, // Bold A-Z, a-z
    { start: 0x1D434, end: 0x1D467, category: 'italic', base: 0x0041 },
    { start: 0x1D49C, end: 0x1D4CF, category: 'script', base: 0x0041 },
    { start: 0x1D504, end: 0x1D537, category: 'fraktur', base: 0x0041 },
    { start: 0x1D670, end: 0x1D6A3, category: 'monospace', base: 0x0041 },
  ];
  
  // Enclosed alphanumerics
  const enclosedRanges = [
    { start: 0x2460, end: 0x2473, category: 'enclosed' }, // Circled numbers
    { start: 0x24B6, end: 0x24EA, category: 'enclosed' }, // Circled letters
    { start: 0x1F130, end: 0x1F149, category: 'enclosed' }, // Squared letters
  ];
  
  const byCategory = new Map<string, { position: number; substitute: string; original: string }[]>();
  
  for (let i = 0; i < text.length; i++) {
    const code = text.codePointAt(i);
    if (!code) continue;
    
    // Skip if this is a surrogate pair continuation
    if (code > 0xFFFF) {
      i++; // Skip next char (low surrogate)
    }
    
    // Check math ranges
    for (const range of mathRanges) {
      if (code >= range.start && code <= range.end) {
        const offset = code - range.start;
        const originalCode = range.base + (offset % 26);
        const original = String.fromCharCode(originalCode);
        
        if (!byCategory.has(range.category)) {
          byCategory.set(range.category, []);
        }
        
        byCategory.get(range.category)!.push({
          position: i,
          substitute: String.fromCodePoint(code),
          original,
        });
      }
    }
    
    // Check enclosed ranges
    for (const range of enclosedRanges) {
      if (code >= range.start && code <= range.end) {
        if (!byCategory.has('enclosed')) {
          byCategory.set('enclosed', []);
        }
        
        byCategory.get('enclosed')!.push({
          position: i,
          substitute: String.fromCodePoint(code),
          original: '?', // Mapping is complex, use placeholder
        });
      }
    }
  }
  
  // Convert to array
  byCategory.forEach((positions, category) => {
    if (positions.length > 0) {
      substitutions.push({
        positions,
        category: category as 'bold' | 'italic' | 'script' | 'fraktur' | 'monospace' | 'enclosed',
        count: positions.length,
      });
    }
  });
  
  return substitutions;
}

/**
 * Detect control character exploits (ANSI, backspace, etc.)
 */
export function detectControlCharacterExploits(text: string): ControlCharExploit | null {
  const sequences: { position: number; sequence: string; description: string }[] = [];
  
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    
    // 1. ANSI Escape sequences (ESC + [)
    if (code === 0x1B) {
      // Check if followed by [
      if (i + 1 < text.length && text.charCodeAt(i + 1) === 0x5B) {
        // Extract the full sequence
        let j = i + 2;
        let seq = '\x1b[';
        while (j < text.length && j < i + 20) {
          const c = text.charCodeAt(j);
          seq += text[j];
          j++;
          // ANSI sequences end with a letter
          if (c >= 0x40 && c <= 0x7E) break;
        }
        
        sequences.push({
          position: i,
          sequence: seq,
          description: `ANSI escape sequence detected. Can control terminal colors, cursor position, or clear screen.`,
        });
      }
    }
    
    // 2. Backspace (BS)
    else if (code === 0x08) {
      sequences.push({
        position: i,
        sequence: '\\b',
        description: `Backspace character. Can be used to visually "erase" preceding characters.`,
      });
    }
    
    // 3. Form Feed (FF)
    else if (code === 0x0C) {
      sequences.push({
        position: i,
        sequence: '\\f',
        description: `Form feed character. Can cause unexpected page breaks or formatting.`,
      });
    }
    
    // 4. Carriage Return without Line Feed
    else if (code === 0x0D) {
      // Check if NOT followed by LF (0x0A)
      if (i + 1 >= text.length || text.charCodeAt(i + 1) !== 0x0A) {
        sequences.push({
          position: i,
          sequence: '\\r',
          description: `Carriage return without line feed. Can be used to overwrite line content.`,
        });
      }
    }
    
    // 5. Other control characters (C0 controls)
    else if (code < 0x20 && code !== 0x09 && code !== 0x0A && code !== 0x0D) {
      sequences.push({
        position: i,
        sequence: `\\x${code.toString(16).padStart(2, '0')}`,
        description: `Control character U+${code.toString(16).toUpperCase().padStart(4, '0')}. May cause unexpected behavior.`,
      });
    }
  }
  
  if (sequences.length === 0) {
    return null;
  }
  
  // Determine risk level
  const hasANSI = sequences.some(s => s.sequence.startsWith('\x1b['));
  const hasBackspace = sequences.some(s => s.sequence === '\\b');
  const count = sequences.length;
  
  let risk: 'MEDIUM' | 'HIGH' | 'CRITICAL' = 'MEDIUM';
  
  if (hasANSI || (hasBackspace && count >= 5)) {
    risk = 'CRITICAL';
  } else if (count >= 10 || hasBackspace) {
    risk = 'HIGH';
  }
  
  // Determine type
  let type: 'ansi_escape' | 'backspace_delete' | 'form_feed' | 'terminal_control' = 'terminal_control';
  
  if (hasANSI) {
    type = 'ansi_escape';
  } else if (hasBackspace) {
    type = 'backspace_delete';
  } else if (sequences.some(s => s.sequence === '\\f')) {
    type = 'form_feed';
  }
  
  return {
    type,
    sequences,
    risk,
  };
}

/**
 * NEW PATTERN DETECTIONS - Phase 3
 */

// Whitespace Abuse Detection
export interface WhitespaceAbuse {
  hasAbuse: boolean;
  issues: {
    type: 'trailing' | 'leading' | 'mixed_types' | 'excessive' | 'tab_mixing';
    positions: number[];
    spaceTypes: string[];
    description: string;
  }[];
}

export function detectWhitespaceAbuse(text: string): WhitespaceAbuse {
  const issues: WhitespaceAbuse['issues'] = [];
  
  // 1. Detect trailing whitespace
  const trailingPositions: number[] = [];
  const lines = text.split('\n');
  let currentPos = 0;
  
  lines.forEach((line) => {
    const trimmedLength = line.trimEnd().length;
    if (trimmedLength < line.length) {
      for (let i = trimmedLength; i < line.length; i++) {
        trailingPositions.push(currentPos + i);
      }
    }
    currentPos += line.length + 1; // +1 for newline
  });
  
  if (trailingPositions.length > 0) {
    issues.push({
      type: 'trailing',
      positions: trailingPositions,
      spaceTypes: [],
      description: `${trailingPositions.length} trailing whitespace character${trailingPositions.length > 1 ? 's' : ''} detected`,
    });
  }
  
  // 2. Detect leading whitespace abuse
  const leadingPositions: number[] = [];
  currentPos = 0;
  lines.forEach((line) => {
    const trimmedStart = line.trimStart();
    const leadingCount = line.length - trimmedStart.length;
    if (leadingCount > 10) { // Flag if more than 10 leading spaces
      for (let i = 0; i < leadingCount; i++) {
        leadingPositions.push(currentPos + i);
      }
    }
    currentPos += line.length + 1;
  });
  
  if (leadingPositions.length > 0) {
    issues.push({
      type: 'leading',
      positions: leadingPositions,
      spaceTypes: [],
      description: `Excessive leading whitespace detected (${leadingPositions.length} characters)`,
    });
  }
  
  // 3. Detect mixed space types
  const spaceTypes = new Map<number, number[]>();
  const spaceChars = [
    { code: 0x0020, name: 'Space' },
    { code: 0x00A0, name: 'No-Break Space' },
    { code: 0x2000, name: 'En Quad' },
    { code: 0x2001, name: 'Em Quad' },
    { code: 0x2002, name: 'En Space' },
    { code: 0x2003, name: 'Em Space' },
    { code: 0x2004, name: 'Three-Per-Em Space' },
    { code: 0x2005, name: 'Four-Per-Em Space' },
    { code: 0x2006, name: 'Six-Per-Em Space' },
    { code: 0x2007, name: 'Figure Space' },
    { code: 0x2008, name: 'Punctuation Space' },
    { code: 0x2009, name: 'Thin Space' },
    { code: 0x200A, name: 'Hair Space' },
    { code: 0x202F, name: 'Narrow No-Break Space' },
    { code: 0x205F, name: 'Medium Mathematical Space' },
    { code: 0x3000, name: 'Ideographic Space' },
  ];
  
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const spaceChar = spaceChars.find(s => s.code === code);
    if (spaceChar) {
      if (!spaceTypes.has(code)) {
        spaceTypes.set(code, []);
      }
      spaceTypes.get(code)!.push(i);
    }
  }
  
  if (spaceTypes.size >= 3) {
    const allPositions: number[] = [];
    const typeNames: string[] = [];
    spaceTypes.forEach((positions, code) => {
      allPositions.push(...positions);
      const spaceChar = spaceChars.find(s => s.code === code);
      if (spaceChar) typeNames.push(spaceChar.name);
    });
    
    issues.push({
      type: 'mixed_types',
      positions: allPositions,
      spaceTypes: typeNames,
      description: `${spaceTypes.size} different space character types mixed in text`,
    });
  }
  
  // 4. Detect tab mixing
  const tabPositions: number[] = [];
  const regularSpacePositions: number[] = [];
  
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) === 0x09) tabPositions.push(i);
    if (text.charCodeAt(i) === 0x20) regularSpacePositions.push(i);
  }
  
  if (tabPositions.length > 0 && regularSpacePositions.length > 0) {
    issues.push({
      type: 'tab_mixing',
      positions: [...tabPositions, ...regularSpacePositions],
      spaceTypes: ['Tab', 'Space'],
      description: `Tabs and spaces mixed (${tabPositions.length} tabs, ${regularSpacePositions.length} spaces)`,
    });
  }
  
  return {
    hasAbuse: issues.length > 0,
    issues,
  };
}

// Character Width Anomalies Detection
export interface WidthAnomaly {
  position: number;
  character: string;
  width: 'fullwidth' | 'halfwidth';
  normalEquivalent: string;
  code: number;
}

export interface CharacterWidthAbuse {
  hasAbuse: boolean;
  anomalies: WidthAnomaly[];
  mixedWidths: boolean;
}

export function detectCharacterWidthAbuse(text: string): CharacterWidthAbuse {
  const anomalies: WidthAnomaly[] = [];
  
  // Full-width alphanumeric range: U+FF01-FF5E
  // Maps to ASCII: U+0021-007E
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const char = text[i];
    
    // Full-width detection (U+FF01-FF5E)
    if (code >= 0xFF01 && code <= 0xFF5E) {
      const normalCode = code - 0xFEE0; // Convert to ASCII
      const normalChar = String.fromCharCode(normalCode);
      
      anomalies.push({
        position: i,
        character: char,
        width: 'fullwidth',
        normalEquivalent: normalChar,
        code,
      });
    }
    
    // Halfwidth Katakana (U+FF65-FF9F) - also suspicious in mixed contexts
    else if (code >= 0xFF65 && code <= 0xFF9F) {
      anomalies.push({
        position: i,
        character: char,
        width: 'halfwidth',
        normalEquivalent: char, // Complex mapping, keep original for now
        code,
      });
    }
  }
  
  // Check for mixed widths (both fullwidth and halfwidth/normal in same text)
  const hasFullwidth = anomalies.some(a => a.width === 'fullwidth');
  const hasNormalAscii = /[A-Za-z0-9]/.test(text);
  const mixedWidths = hasFullwidth && hasNormalAscii;
  
  return {
    hasAbuse: anomalies.length > 0,
    anomalies,
    mixedWidths,
  };
}

// Look-alike Digits Detection
export interface LookalikeDigit {
  position: number;
  character: string;
  digitType: 'superscript' | 'subscript' | 'circled' | 'parenthesized' | 'roman' | 'fraction';
  normalEquivalent: string;
  code: number;
}

export interface LookalikeDigitsAbuse {
  hasAbuse: boolean;
  digits: LookalikeDigit[];
}

export function detectLookalikeDigits(text: string): LookalikeDigitsAbuse {
  const digits: LookalikeDigit[] = [];
  
  // Superscript digits mapping
  const superscripts: { [key: number]: string } = {
    0x00B9: '1', 0x00B2: '2', 0x00B3: '3',
    0x2074: '4', 0x2075: '5', 0x2076: '6', 0x2077: '7', 0x2078: '8', 0x2079: '9', 0x2070: '0',
  };
  
  // Subscript digits mapping (U+2080-2089)
  const subscripts: { [key: number]: string } = {};
  for (let i = 0; i <= 9; i++) {
    subscripts[0x2080 + i] = i.toString();
  }
  
  // Circled numbers (U+2460-2473): ①-⑳
  // Parenthesized numbers (U+2474-2487): ⑴-⑵0
  
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const char = text[i];
    
    // Check superscripts
    if (code in superscripts) {
      digits.push({
        position: i,
        character: char,
        digitType: 'superscript',
        normalEquivalent: superscripts[code],
        code,
      });
    }
    // Check subscripts
    else if (code in subscripts) {
      digits.push({
        position: i,
        character: char,
        digitType: 'subscript',
        normalEquivalent: subscripts[code],
        code,
      });
    }
    // Check circled (U+2460-2473)
    else if (code >= 0x2460 && code <= 0x2473) {
      const num = (code - 0x2460 + 1).toString();
      digits.push({
        position: i,
        character: char,
        digitType: 'circled',
        normalEquivalent: num,
        code,
      });
    }
    // Check parenthesized (U+2474-2487)
    else if (code >= 0x2474 && code <= 0x2487) {
      const num = (code - 0x2474 + 1).toString();
      digits.push({
        position: i,
        character: char,
        digitType: 'parenthesized',
        normalEquivalent: num,
        code,
      });
    }
    // Check Roman numerals (U+2160-217F)
    else if (code >= 0x2160 && code <= 0x217F) {
      digits.push({
        position: i,
        character: char,
        digitType: 'roman',
        normalEquivalent: '?', // Complex conversion
        code,
      });
    }
    // Check common fractions (U+00BC-00BE, U+2153-215E)
    else if ((code >= 0x00BC && code <= 0x00BE) || (code >= 0x2153 && code <= 0x215E)) {
      digits.push({
        position: i,
        character: char,
        digitType: 'fraction',
        normalEquivalent: '?', // Complex conversion
        code,
      });
    }
  }
  
  return {
    hasAbuse: digits.length > 0,
    digits,
  };
}

// Quotation Mark Confusion Detection
export interface QuotationAnomaly {
  position: number;
  character: string;
  type: 'typographic_double' | 'typographic_single' | 'guillemet' | 'prime' | 'grave_acute';
  standardEquivalent: string;
  code: number;
}

export interface QuotationConfusion {
  hasConfusion: boolean;
  anomalies: QuotationAnomaly[];
  potentialSQLInjection: boolean;
  sqlInjectionConfidence?: number; // 0-100, confidence score for SQL injection detection
}

export function detectQuotationConfusion(text: string): QuotationConfusion {
  const anomalies: QuotationAnomaly[] = [];
  
  const quotationMap: { code: number; type: QuotationAnomaly['type']; equiv: string }[] = [
    // Double quotes
    { code: 0x201C, type: 'typographic_double', equiv: '"' }, // Left double quotation mark
    { code: 0x201D, type: 'typographic_double', equiv: '"' }, // Right double quotation mark
    { code: 0x201E, type: 'typographic_double', equiv: '"' }, // Double low-9 quotation mark
    { code: 0x00AB, type: 'guillemet', equiv: '"' }, // Left guillemet «
    { code: 0x00BB, type: 'guillemet', equiv: '"' }, // Right guillemet »
    
    // Single quotes
    { code: 0x2018, type: 'typographic_single', equiv: "'" }, // Left single quotation mark
    { code: 0x2019, type: 'typographic_single', equiv: "'" }, // Right single quotation mark
    { code: 0x201A, type: 'typographic_single', equiv: "'" }, // Single low-9 quotation mark
    { code: 0x2039, type: 'guillemet', equiv: "'" }, // Left single guillemet ‹
    { code: 0x203A, type: 'guillemet', equiv: "'" }, // Right single guillemet ›
    
    // Accents/Primes
    { code: 0x0060, type: 'grave_acute', equiv: "'" }, // Grave accent `
    { code: 0x00B4, type: 'grave_acute', equiv: "'" }, // Acute accent ´
    { code: 0x2032, type: 'prime', equiv: "'" }, // Prime ′
    { code: 0x2033, type: 'prime', equiv: '"' }, // Double prime ″
  ];
  
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const char = text[i];
    
    const match = quotationMap.find(q => q.code === code);
    if (match) {
      anomalies.push({
        position: i,
        character: char,
        type: match.type,
        standardEquivalent: match.equiv,
        code,
      });
    }
  }
  
  // Check for potential SQL injection using proximity-based detection
  // Require 3+ SQL keywords AND quotes within proximity to reduce false positives on prose
  let potentialSQLInjection = false;
  let sqlInjectionConfidence: number | undefined = undefined;
  
  if (anomalies.length > 0) {
    // Count SQL keywords - use more specific keywords to avoid common words
    const sqlKeywordPattern = /\b(SELECT|INSERT|UPDATE|DELETE|DROP|TRUNCATE|ALTER|CREATE|UNION|EXEC|EXECUTE)\b/gi;
    const commonWordsPattern = /\b(WHERE|FROM|AND|OR|INTO|VALUES|SET|TABLE)\b/gi;
    
    const specificKeywords = text.match(sqlKeywordPattern) || [];
    const commonWords = text.match(commonWordsPattern) || [];
    const totalKeywords = specificKeywords.length + commonWords.length;
    
    // Check for highly suspicious patterns
    const suspiciousPatterns = [
      /;\s*(DROP|DELETE|TRUNCATE)/i,  // Statement chaining
      /UNION\s+SELECT/i,               // UNION-based injection
      /'\s*OR\s*'1'\s*=\s*'1/i,       // Classic OR injection
      /--/,                            // SQL comment
      /\/\*/,                          // Multi-line comment
    ];
    
    const hasSuspiciousPattern = suspiciousPatterns.some(pattern => pattern.test(text));
    
    // Check proximity: are quotes near SQL keywords?
    let quotesNearKeywords = 0;
    let diacriticUsageCount = 0; // Count grave/acute used as diacritics
    
    anomalies.forEach(anomaly => {
      const windowStart = Math.max(0, anomaly.position - 50);
      const windowEnd = Math.min(text.length, anomaly.position + 50);
      const window = text.substring(windowStart, windowEnd);
      
      // Check if grave/acute accent is used as a diacritic (adjacent to letters)
      if (anomaly.type === 'grave_acute') {
        const prevChar = anomaly.position > 0 ? text[anomaly.position - 1] : '';
        const nextChar = anomaly.position < text.length - 1 ? text[anomaly.position + 1] : '';
        
        // If adjacent to a letter, it's likely a diacritic, not a quote
        if (/[a-zA-Z]/.test(prevChar) || /[a-zA-Z]/.test(nextChar)) {
          diacriticUsageCount++;
          return; // Skip counting this as a quote near keywords
        }
      }
      
      if (sqlKeywordPattern.test(window) || commonWordsPattern.test(window)) {
        quotesNearKeywords++;
      }
    });
    
    // Calculate confidence score
    let confidence = 0;
    
    // Heavy penalty if most "quotes" are actually diacritics
    const diacriticRatio = anomalies.length > 0 ? diacriticUsageCount / anomalies.length : 0;
    const isDiacriticHeavy = diacriticRatio > 0.5; // More than 50% are diacritics
    
    // Highly suspicious patterns = instant high confidence (unless all diacritics)
    if (hasSuspiciousPattern && !isDiacriticHeavy) {
      confidence = 90;
    } 
    // Multiple specific SQL keywords (SELECT, INSERT, etc.) with quotes nearby
    else if (specificKeywords.length >= 2 && quotesNearKeywords >= 2 && !isDiacriticHeavy) {
      confidence = 70 + (specificKeywords.length * 5);
    }
    // Many keywords overall (3+) with quotes in proximity
    else if (totalKeywords >= 3 && quotesNearKeywords >= 3 && !isDiacriticHeavy) {
      confidence = 50 + (totalKeywords * 3);
    }
    // Some keywords but might be prose
    else if (totalKeywords >= 3 && !isDiacriticHeavy) {
      confidence = 25;
    }
    // If text is diacritic-heavy (linguistic examples, names), drastically reduce confidence
    else if (isDiacriticHeavy) {
      // Only very high confidence if we have actual SQL patterns despite diacritics
      if (hasSuspiciousPattern && quotesNearKeywords > 5) {
        confidence = 60; // Still suspicious but recognize context
      } else {
        confidence = 0; // Likely linguistic content, not SQL injection
      }
    }
    
    // Additional penalty for pure diacritic usage (e.g., Unicode examples)
    if (diacriticUsageCount === anomalies.length && totalKeywords === 0) {
      confidence = 0; // All "quotes" are diacritics and no SQL keywords
    }
    
    // Cap confidence at 95
    confidence = Math.min(95, confidence);
    
    // Only flag if confidence is at least 50
    potentialSQLInjection = confidence >= 50;
    sqlInjectionConfidence = confidence > 0 ? confidence : undefined;
  }
  
  return {
    hasConfusion: anomalies.length > 0,
    anomalies,
    potentialSQLInjection,
    sqlInjectionConfidence,
  };
}

// ============================================================================
// TIER 2 PATTERNS: Advanced Detection
// ============================================================================

// Frequency Analysis & Anomaly Detection
export interface FrequencyAnalysis {
  hasAnomaly: boolean;
  entropy: number; // 0-8 bits (0=no variation, 8=maximum randomness)
  characterDistribution: { char: string; count: number; frequency: number }[];
  suspiciousPatterns: {
    type: 'base64' | 'high_entropy' | 'uniform_distribution' | 'non_textual';
    confidence: number; // 0-100
    description: string;
    sample?: string;
  }[];
  languageMixing?: { languages: string[]; suspiciousContexts: number[] };
}

export function analyzeFrequency(text: string): FrequencyAnalysis {
  const suspiciousPatterns: FrequencyAnalysis['suspiciousPatterns'] = [];
  
  // 1. Calculate Shannon Entropy
  const charCount: Map<string, number> = new Map();
  for (const char of text) {
    charCount.set(char, (charCount.get(char) || 0) + 1);
  }
  
  let entropy = 0;
  for (const count of charCount.values()) {
    const probability = count / text.length;
    entropy -= probability * Math.log2(probability);
  }
  
  // 2. Build character distribution
  const characterDistribution = Array.from(charCount.entries())
    .map(([char, count]) => ({
      char,
      count,
      frequency: count / text.length,
    }))
    .sort((a, b) => b.count - a.count);
  
  // 3. Detect Base64 encoding
  const base64Regex = /^[A-Za-z0-9+/=]{20,}$/;
  const base64Match = text.match(base64Regex);
  if (base64Match && entropy > 5.5) {
    suspiciousPatterns.push({
      type: 'base64',
      confidence: 90,
      description: 'Base64 encoding detected - high entropy with limited character set',
      sample: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
    });
  }
  
  // 4. Detect high entropy (potential encryption)
  if (entropy > 7.0 && text.length > 50) {
    suspiciousPatterns.push({
      type: 'high_entropy',
      confidence: Math.min(95, Math.round((entropy / 8) * 100)),
      description: `Very high entropy (${entropy.toFixed(2)} bits) suggests encryption or random data`,
    });
  }
  
  // 5. Detect uniform distribution (steganography indicator)
  const avgFrequency = 1 / charCount.size;
  const uniformityScore = characterDistribution.reduce((sum, item) => {
    return sum + Math.abs(item.frequency - avgFrequency);
  }, 0) / charCount.size;
  
  // Check for unusually uniform distribution combined with high entropy
  // Only flag extremely uniform distributions (< 0.005) that are characteristic of encoded/encrypted data
  // Natural text has much higher uniformity scores (typically > 0.015)
  if (uniformityScore < 0.005 && text.length > 100 && charCount.size > 20) {
    // Require high entropy to avoid false positives - encrypted/encoded data has high randomness
    if (entropy > 6.5) {
      // Get top characters to show distribution
      const topChars = characterDistribution.slice(0, 5)
        .map(c => `'${c.char}': ${(c.frequency * 100).toFixed(1)}%`)
        .join(', ');
      
      suspiciousPatterns.push({
        type: 'uniform_distribution',
        // Scale confidence based on how uniform the distribution is (0.005 = 0%, 0 = 100%)
        confidence: Math.round(90 * (1 - uniformityScore / 0.005)),
        description: `Unusually uniform character distribution may indicate encoded/encrypted data. ${charCount.size} unique chars, uniformity: ${(uniformityScore * 100).toFixed(2)}%. Top chars: ${topChars}`,
      });
    }
  }
  
  // 6. Detect non-textual patterns (too many numbers, symbols)
  const alphaCount = (text.match(/[a-zA-Z]/g) || []).length;
  const digitCount = (text.match(/[0-9]/g) || []).length;
  const spaceCount = (text.match(/\s/g) || []).length;
  const alphaRatio = alphaCount / text.length;
  const digitRatio = digitCount / text.length;
  
  if (digitRatio > 0.5 && alphaRatio < 0.1 && spaceCount < text.length * 0.05) {
    suspiciousPatterns.push({
      type: 'non_textual',
      confidence: 70,
      description: `High digit ratio (${(digitRatio * 100).toFixed(1)}%) with low spaces suggests encoded data`,
    });
  }
  
  return {
    hasAnomaly: suspiciousPatterns.length > 0,
    entropy,
    characterDistribution: characterDistribution.slice(0, 20), // Top 20 chars
    suspiciousPatterns,
  };
}

// Domain-Specific Pattern Detection
export interface DomainPattern {
  type: 'email' | 'ip' | 'phone' | 'credit_card';
  position: number;
  value: string;
  issues: string[];
  normalized?: string;
}

export interface DomainSpecificAbuse {
  hasAbuse: boolean;
  patterns: DomainPattern[];
  totalAnomalies: number;
}

export function detectDomainSpecificPatterns(text: string): DomainSpecificAbuse {
  const patterns: DomainPattern[] = [];
  
  // 1. Email address with unusual characters
  const emailRegex = /[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,}/gi;
  let emailMatch;
  while ((emailMatch = emailRegex.exec(text)) !== null) {
    const email = emailMatch[0];
    const issues: string[] = [];
    
    // Check for full-width characters
    if (/[\uFF00-\uFFEF]/.test(email)) {
      issues.push('Full-width characters detected in email domain');
    }
    
    // Check for homoglyphs in domain
    const domain = email.split('@')[1];
    if (domain && /[а-яА-Я]/.test(domain)) {
      issues.push('Cyrillic characters in domain (possible spoofing)');
    }
    
    if (issues.length > 0) {
      patterns.push({
        type: 'email',
        position: emailMatch.index,
        value: email,
        issues,
      });
    }
  }
  
  // 2. IP address with look-alike characters
  const ipRegex = /\b(?:\d{1,3}[.．·‧]){3}\d{1,3}\b/g;
  let ipMatch;
  while ((ipMatch = ipRegex.exec(text)) !== null) {
    const ip = ipMatch[0];
    const issues: string[] = [];
    
    // Check for non-standard dots
    if (/[．·‧]/.test(ip)) {
      issues.push('Non-standard separator characters (U+FF0E, U+00B7, U+2027) instead of period');
      const normalized = ip.replace(/[．·‧]/g, '.');
      patterns.push({
        type: 'ip',
        position: ipMatch.index,
        value: ip,
        issues,
        normalized,
      });
    }
  }
  
  // 3. Phone numbers with Unicode digits
  const phoneRegex = /[\d⓪①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳₀₁₂₃₄₅₆₇₈₉¹²³⁴⁵⁶⁷⁸⁹⁰]{7,}/g;
  let phoneMatch;
  while ((phoneMatch = phoneRegex.exec(text)) !== null) {
    const phone = phoneMatch[0];
    const issues: string[] = [];
    
    // Check for non-standard digits
    if (/[⓪①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳₀₁₂₃₄₅₆₇₈₉¹²³⁴⁵⁶⁷⁸⁹⁰]/.test(phone)) {
      issues.push('Unicode digit variants detected (circled, subscript, superscript)');
      patterns.push({
        type: 'phone',
        position: phoneMatch.index,
        value: phone,
        issues,
      });
    }
  }
  
  // 4. Credit card patterns with Unicode digits
  const ccRegex = /[\d⁰¹²³⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉]{13,19}/g;
  let ccMatch;
  while ((ccMatch = ccRegex.exec(text)) !== null) {
    const cc = ccMatch[0];
    const issues: string[] = [];
    
    // Check for superscript/subscript digits
    if (/[⁰¹²³⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉]/.test(cc)) {
      issues.push('Superscript or subscript digits in credit card number');
      patterns.push({
        type: 'credit_card',
        position: ccMatch.index,
        value: cc,
        issues,
      });
    }
  }
  
  return {
    hasAbuse: patterns.length > 0,
    patterns,
    totalAnomalies: patterns.reduce((sum, p) => sum + p.issues.length, 0),
  };
}

// String Length Manipulation Detection
export interface LengthDiscrepancy {
  position: number;
  visibleLength: number; // Grapheme cluster count
  codePointLength: number; // Actual code point count
  byteLength: number; // UTF-8 byte count
  content: string;
  description: string;
}

export interface StringLengthAbuse {
  hasAbuse: boolean;
  totalGraphemes: number;
  totalCodePoints: number;
  totalBytes: number;
  discrepancies: LengthDiscrepancy[];
  ratio: number; // codePoints / graphemes (>1.5 is suspicious)
}

export function detectLengthManipulation(text: string): StringLengthAbuse {
  const discrepancies: LengthDiscrepancy[] = [];
  
  // Use Intl.Segmenter for proper grapheme cluster counting (if available)
  let graphemeCount = 0;
  
  // Simplified grapheme counting - count combined characters
  let i = 0;
  while (i < text.length) {
    let graphemeLen = 1;
    
    // Check for combining marks
    let j = i + 1;
    while (j < text.length) {
      const nextCode = text.charCodeAt(j);
      // Combining Diacritical Marks (0x0300-0x036F)
      // Combining Marks for Symbols (0x20D0-0x20FF)
      // Variation Selectors (0xFE00-0xFE0F)
      // Zero-width characters
      if ((nextCode >= 0x0300 && nextCode <= 0x036F) ||
          (nextCode >= 0x20D0 && nextCode <= 0x20FF) ||
          (nextCode >= 0xFE00 && nextCode <= 0xFE0F) ||
          nextCode === 0x200D || nextCode === 0x200C || nextCode === 0x200B) {
        graphemeLen++;
        j++;
      } else {
        break;
      }
    }
    
    // Flag multi-codepoint graphemes
    if (graphemeLen > 1) {
      const grapheme = text.substring(i, j);
      const codePoints = Array.from(grapheme).length;
      const byteLength = new TextEncoder().encode(grapheme).length;
      let description = '';
      
      if (/[\u200D]/.test(grapheme)) {
        description = 'ZWJ emoji sequence (displays as 1, stores as multiple)';
      } else if (/[\u0300-\u036F\u20D0-\u20FF]/.test(grapheme)) {
        description = 'Combining diacritics (displays as 1, stores as multiple)';
      } else if (/[\uFE00-\uFE0F]/.test(grapheme)) {
        description = 'Variation selector (changes appearance but adds length)';
      } else {
        description = 'Multi-codepoint grapheme cluster';
      }
      
      discrepancies.push({
        position: i,
        visibleLength: 1,
        codePointLength: codePoints,
        byteLength,
        content: grapheme,
        description,
      });
    }
    
    graphemeCount++;
    i = j;
  }
  
  const codePointCount = Array.from(text).length;
  const byteCount = new TextEncoder().encode(text).length;
  const ratio = codePointCount / Math.max(1, graphemeCount);
  
  return {
    hasAbuse: ratio > 1.5 || discrepancies.length > 5,
    totalGraphemes: graphemeCount,
    totalCodePoints: codePointCount,
    totalBytes: byteCount,
    discrepancies: discrepancies.slice(0, 20), // Limit to first 20
    ratio,
  };
}

// ===== PUA & 2024 UNICODE THREAT DETECTION FUNCTIONS =====

// Helper: Check if PUA characters are clustered (icon fonts) vs scattered (steganography)
function hasIconFontClustering(positions: number[]): boolean {
  if (positions.length < 3) return false;
  
  const gaps = [];
  for (let i = 0; i < positions.length - 1; i++) {
    gaps.push(positions[i + 1] - positions[i]);
  }
  
  const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const maxGap = Math.max(...gaps);
  
  // Icon fonts: avg gap < 5, max gap < 20 (clustered)
  // Steganography: gaps vary widely (0-200+)
  return avgGap < 5 && maxGap < 20;
}

// Helper: Detect steganography patterns in positions
function detectSteganographyPatterns(text: string, positions: number[]): string[] {
  const patterns: string[] = [];
  
  if (positions.length < 8) return patterns;
  
  // Pattern 1: Regular interval encoding (every N characters)
  const gaps = [];
  for (let i = 0; i < positions.length - 1; i++) {
    gaps.push(positions[i + 1] - positions[i]);
  }
  
  const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const variance = gaps.reduce((sum, g) => sum + Math.pow(g - avgGap, 2), 0) / gaps.length;
  const stdDev = Math.sqrt(variance);
  const coefficientOfVariation = stdDev / avgGap;
  
  // Low coefficient of variation = regular pattern = likely encoding
  if (coefficientOfVariation < 0.3) {
    patterns.push('regular_interval_encoding');
  }
  
  // Pattern 2: Binary encoding (only 1-2 specific PUA characters)
  const uniqueChars = new Set(positions.map(pos => text.charCodeAt(pos)));
  if (uniqueChars.size <= 2 && positions.length > 8) {
    patterns.push('binary_encoding_likely');
  }
  
  // Pattern 3: Isolated clusters (PUA at start, middle, or end)
  const firstQuarter = positions.filter(p => p < text.length / 4).length;
  const lastQuarter = positions.filter(p => p > text.length * 0.75).length;
  const ratio1 = firstQuarter / positions.length;
  const ratio2 = lastQuarter / positions.length;
  
  if (ratio1 > 0.85 || ratio2 > 0.85) {
    patterns.push('isolated_cluster');
  }
  
  return patterns;
}

/**
 * Detect Private Use Area characters and assess risk level
 */
export function detectPUACharacters(text: string): PUADetection {
  const positions: number[] = [];
  const rangeMap = new Map<string, number>();
  
  // Scan for all PUA characters
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (isPUACharacter(code)) {
      positions.push(i);
      const category = getPUACategory(code);
      rangeMap.set(category, (rangeMap.get(category) || 0) + 1);
    }
  }
  
  // Build range analysis
  const ranges = Array.from(rangeMap.entries()).map(([range, count]) => ({
    range,
    count,
    percentage: (count / text.length) * 100,
  }));
  
  // Calculate density
  const density = positions.length / text.length;
  
  // Analyze patterns - IMPROVED
  const patterns = [];
  
  // Advanced steganography pattern detection
  const steganographyPatterns = detectSteganographyPatterns(text, positions);
  if (steganographyPatterns.includes('regular_interval_encoding')) {
    patterns.push({ type: 'regular_interval_encoding', likelihood: 'high' as const });
  }
  if (steganographyPatterns.includes('binary_encoding_likely')) {
    patterns.push({ type: 'binary_encoding_likely', likelihood: 'high' as const });
  }
  if (steganographyPatterns.includes('isolated_cluster')) {
    patterns.push({ type: 'isolated_cluster', likelihood: 'high' as const });
  }
  
  // General density-based patterns
  if (density > 0.05 && density < 0.15) patterns.push({ type: 'moderate_density', likelihood: 'medium' as const });
  if (density >= 0.15 && density < 0.25) patterns.push({ type: 'high_density', likelihood: 'high' as const });
  if (density >= 0.25) patterns.push({ type: 'steganography_risk', likelihood: 'high' as const });
  
  // IMPROVED Icon font detection with multi-range and clustering
  const iconFontRanges = {
    fontAwesome: { start: 0xF000, end: 0xF2FF, count: 0 },
    materialDesign: { start: 0xE000, end: 0xE3FF, count: 0 },
    ionicons: { start: 0xE400, end: 0xE4FF, count: 0 },
  };
  
  for (const pos of positions) {
    const code = text.charCodeAt(pos);
    if (code >= iconFontRanges.fontAwesome.start && code <= iconFontRanges.fontAwesome.end) {
      iconFontRanges.fontAwesome.count++;
    } else if (code >= iconFontRanges.materialDesign.start && code <= iconFontRanges.materialDesign.end) {
      iconFontRanges.materialDesign.count++;
    } else if (code >= iconFontRanges.ionicons.start && code <= iconFontRanges.ionicons.end) {
      iconFontRanges.ionicons.count++;
    }
  }
  
  const singleRangePercent = Math.max(
    iconFontRanges.fontAwesome.count / positions.length,
    iconFontRanges.materialDesign.count / positions.length,
    iconFontRanges.ionicons.count / positions.length
  );
  
  // IMPROVED: Must have 90%+ from one range AND be clustered to be icon font
  const isClustered = hasIconFontClustering(positions);
  if (singleRangePercent >= 0.9 && isClustered) {
    patterns.push({ type: 'icon_font_likely', likelihood: 'high' as const });
  }
  
  // Determine risk level - IMPROVED logic
  let riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' = 'LOW';
  let suggestedCategory: 'icon_font' | 'cjk_gaiji' | 'steganography' | 'unknown' = 'unknown';
  
  if (positions.length === 0) {
    riskLevel = 'LOW';
  } else if (density < 0.01) {
    riskLevel = 'LOW';
    suggestedCategory = 'unknown';
  } else if (singleRangePercent >= 0.9 && isClustered) {
    // Likely icon font
    riskLevel = 'LOW';
    suggestedCategory = 'icon_font';
  } else if (density < 0.05) {
    riskLevel = 'MEDIUM';
    suggestedCategory = steganographyPatterns.length > 0 ? 'steganography' : 'unknown';
  } else if (density < 0.20) {
    riskLevel = 'HIGH';
    suggestedCategory = 'steganography';
  } else {
    riskLevel = 'CRITICAL';
    suggestedCategory = 'steganography';
  }
  
  return {
    hasPUA: positions.length > 0,
    totalPUACount: positions.length,
    density,
    ranges,
    patterns,
    positions,
    riskLevel,
    suggestedCategory,
  };
}

/**
 * Detect Fullwidth characters that may exploit CVE-2024-4577 and similar vulnerabilities
 */
export function detectFullwidthExploitation(text: string): FullwidthExploitation {
  const dangerousChars: FullwidthExploitation['dangerousChars'] = [];
  
  // IMPROVED: Comprehensive fullwidth vulnerability mapping
  const dangerousMap: Record<number, { normalize: string; cve: string[]; severity: 'MEDIUM' | 'HIGH' | 'CRITICAL' }> = {
    0xFF0E: { normalize: '.', cve: ['CVE-2024-4577'], severity: 'CRITICAL' },      // Fullwidth Full Stop
    0xFF3C: { normalize: '\\', cve: ['CVE-2024-4577'], severity: 'CRITICAL' },     // Fullwidth Backslash
    0xFF1C: { normalize: '<', cve: ['CVE-2024-34078'], severity: 'HIGH' },         // Fullwidth Less-Than
    0xFF1E: { normalize: '>', cve: ['CVE-2024-34078'], severity: 'HIGH' },         // Fullwidth Greater-Than
    0xFF0F: { normalize: '/', cve: ['CVE-2024-4577'], severity: 'HIGH' },          // Fullwidth Solidus
    0xFF1A: { normalize: ':', cve: ['CVE-2024-43093'], severity: 'MEDIUM' },       // Fullwidth Colon
    0xFF0A: { normalize: '*', cve: ['CVE-2024-4577'], severity: 'MEDIUM' },        // Fullwidth Asterisk
    0xFF1F: { normalize: '?', cve: [], severity: 'MEDIUM' },                       // Fullwidth Question Mark
  };
  
  const detectedPositions: number[] = [];
  
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code in dangerousMap) {
      const mapping = dangerousMap[code];
      dangerousChars.push({
        position: i,
        char: text[i],
        code: `U+${code.toString(16).toUpperCase().padStart(4, '0')}`,
        normalizesTo: mapping.normalize,
      });
      detectedPositions.push(i);
    }
  }
  
  // IMPROVED: Detect consecutive fullwidth combinations (path traversal patterns)
  let cveRelated: 'CVE-2024-4577' | 'CVE-2024-34078' | 'none' = dangerousChars.length === 0 ? 'none' : 'CVE-2024-4577';
  
  // Check for specific dangerous combinations
  for (let i = 0; i < detectedPositions.length - 1; i++) {
    if (detectedPositions[i + 1] - detectedPositions[i] === 1) {  // Consecutive
      const combo = text[detectedPositions[i]] + text[detectedPositions[i + 1]];
      
      // Known dangerous patterns
      if (combo === '．．' || combo === '／／' || combo === '＼＼') {
        cveRelated = 'CVE-2024-4577';  // Path traversal
      } else if (combo === '＜' || combo === '＞') {
        cveRelated = 'CVE-2024-34078';  // HTML injection
      }
    }
  }
  
  const riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' = 
    dangerousChars.length === 0 ? 'LOW' :
    dangerousChars.length === 1 ? 'MEDIUM' :
    dangerousChars.length <= 5 ? 'HIGH' : 'CRITICAL';
  
  return {
    hasFullwidth: dangerousChars.length > 0,
    dangerousChars,
    cveRelated,
    riskLevel,
  };
}

/**
 * Detect Tag Block character anomalies (U+E0000–U+E007F)
 */
export function detectTagBlockAnomalies(text: string): TagBlockAnomalies {
  const positions: number[] = [];
  
  // Use codePointAt instead of charCodeAt to handle supplementary planes
  for (let i = 0; i < text.length; i++) {
    const code = text.codePointAt(i);
    if (code && isTagBlockCharacter(code)) {
      positions.push(i);
      // Skip the second byte of surrogate pair if present
      if (code > 0xFFFF) i++;
    }
  }
  
  // Check if in emoji context (look for emoji patterns nearby)
  const inEmojiContext = false;
  let suspiciousPatterns = false;
  
  if (positions.length > 0) {
    // Check for emoji indicators - look for extended unicode characters
    for (const pos of positions) {
      const start = Math.max(0, pos - 10);
      const end = Math.min(text.length, pos + 10);
      const window = text.substring(start, end);
      
      // Check for extended unicode characters (emoji-like patterns)
      // eslint-disable-next-line no-control-regex
      if (/[\uD800-\uDBFF]|[\uE0000-\uE007F]|[^\x00-\x7F]/.test(window)) {
        // If we found tag block chars themselves, don't mark as emoji context
        // Tag blocks are suspicious on their own, especially in clusters
      }
    }
    
    // Check for suspicious clustering (multiple tag chars in small region)
    if (positions.length > 3) {
      const gaps = [];
      for (let i = 0; i < positions.length - 1; i++) {
        gaps.push(positions[i + 1] - positions[i]);
      }
      const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
      // Tag block chars close together = suspicious (they're normally rare)
      if (avgGap < 10) suspiciousPatterns = true;
    }
    
    // Tag block chars near text (not just emoji) = suspicious
    // Check if surrounded by regular text (not emoji)
    if (positions.length > 0) {
      for (const pos of positions) {
        const before = text.substring(Math.max(0, pos - 20), pos);
        const after = text.substring(pos + 1, Math.min(text.length, pos + 21));
        
        // If surrounded by normal text (not emoji), it's a phishing/injection indicator
        if (/[a-zA-Z0-9]/.test(before) || /[a-zA-Z0-9]/.test(after)) {
          suspiciousPatterns = true;
          break;
        }
      }
    }
  }
  
  // Revised risk assessment: Tag block chars themselves are HIGH/CRITICAL risk
  // They should never appear in normal text
  const riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' =
    positions.length === 0 ? 'LOW' :
    positions.length === 1 ? 'MEDIUM' :
    suspiciousPatterns ? 'CRITICAL' :
    positions.length > 1 ? 'HIGH' : 'MEDIUM';
  
  return {
    hasTagBlockChars: positions.length > 0,
    count: positions.length,
    positions,
    inEmojiContext,
    suspiciousPatterns,
    riskLevel,
  };
}

/**
 * Analyze emoji composition for token expansion attacks and 2024 emoji attack vectors
 */
export function analyzeEmojiTokens(text: string): EmojiTokenAnalysis {
  // Complex emoji regex with ZWJ sequences
  // Match extended emoji ranges: emotional faces, symbols, etc.
  const emojiSequences: EmojiTokenAnalysis['emojiSequences'] = [];
  
  // Simple approach: find ZWJ sequences (Zero-Width Joiner is U+200D)
  let i = 0;
  while (i < text.length) {
    const code = text.charCodeAt(i);
    // Look for emoji-like characters followed by ZWJ
    if (code >= 0x1F300 && code <= 0x1F9FF) {
      let componentCount = 1;
      let j = i + 1;
      
      // Count ZWJ sequences
      while (j < text.length && text.charCodeAt(j) === 0x200D) {
        componentCount++;
        j += 2; // Skip the ZWJ and next character
      }
      
      if (componentCount > 1) {
        const sequence = text.substring(i, j);
        emojiSequences.push({
          position: i,
          sequence,
          componentCount,
          estimatedTokens: componentCount,
        });
      }
    }
    i++;
  }
  
  // Estimate token count (rough approximation)
  const estimatedTokenCount = text.length / 4 + emojiSequences.reduce((sum, e) => sum + e.componentCount, 0);
  
  // Token expansion risk: if complex emojis are 20%+ of content
  const tokenExpansionRisk = (emojiSequences.length / text.length) > 0.2;
  
  const riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' =
    emojiSequences.length === 0 ? 'LOW' :
    emojiSequences.length <= 5 ? 'MEDIUM' :
    tokenExpansionRisk ? 'CRITICAL' : 'HIGH';
  
  return {
    hasComplexEmoji: emojiSequences.length > 0,
    emojiSequences,
    tokenExpansionRisk,
    estimatedTokenCount,
    riskLevel,
  };
}


/**
 * NTFS Alternate Data Stream (ADS) Detection
 * @internal - Currently unused, kept for potential future integration into security analysis
 */
export interface NTFSAlternateDataStream {
  hasADS: boolean;
  streams: {
    position: number;
    filename: string;
    streamname: string;
    riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    description: string;
  }[];
}

// Function kept for potential future integration into security analysis
// @ts-expect-error - Function intentionally kept unused for future integration
const _detectNTFSAlternateDataStream = function detectNTFSAlternateDataStream(_text: string): NTFSAlternateDataStream {
  const streams: NTFSAlternateDataStream['streams'] = [];
  const adsRegex = /([a-zA-Z0-9_.\\-]+):([a-zA-Z0-9_.\\-]+)/g;
  let match;

  const suspiciousStreamNames = ['malware', 'backdoor', 'payload', 'shell', 'cmd', 'exec', 'hidden'];
  
  while ((match = adsRegex.exec(_text)) !== null) {
    const filename = match[1];
    const streamname = match[2];
    
    if (['http', 'https', 'ftp', 'file'].includes(streamname.toLowerCase())) continue;
    
    let riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' = 'MEDIUM';
    let description = 'NTFS Alternate Data Stream detected';
    
    if (suspiciousStreamNames.some(name => streamname.toLowerCase().includes(name))) {
      riskLevel = 'CRITICAL';
      description = `Suspicious ADS stream: "${streamname}" indicates potential malware`;
    } else if (streamname.length > 20) {
      riskLevel = 'HIGH';
      description = `Long ADS stream name (${streamname.length} chars) - may hide data`;
    }
    
    streams.push({ position: match.index, filename, streamname, riskLevel, description });
  }
  
  return { hasADS: streams.length > 0, streams };
};

/**
 * RTL Text Shaping Attack Detection
 * @internal - Currently unused, kept for potential future integration into security analysis
 */
export interface RTLShapingAttack {
  hasRTLAttack: boolean;
  attacks: {
    position: number;
    type: 'rtl_override' | 'mixed_direction';
    severity: 'MEDIUM' | 'HIGH' | 'CRITICAL';
    description: string;
  }[];
}

// Function kept for potential future integration into security analysis
// @ts-expect-error - Function intentionally kept unused for future integration
const _detectRTLShaping = function detectRTLShaping(_text: string): RTLShapingAttack {
  const attacks: RTLShapingAttack['attacks'] = [];
  const rtlMarkers = [0x202A, 0x202B, 0x202C, 0x202D, 0x202E, 0x2066, 0x2067, 0x2068];
  
  for (let i = 0; i < _text.length; i++) {
    const code = _text.charCodeAt(i);
    
    if (rtlMarkers.includes(code)) {
      if ((code === 0x202E || code === 0x202B) && /[a-zA-Z0-9]/.test(_text[i + 1] || '')) {
        attacks.push({
          position: i,
          type: 'rtl_override',
          severity: 'CRITICAL',
          description: `RTL override marker (U+${code.toString(16).toUpperCase().padStart(4, '0')}) - can reverse domain display`,
        });
      }
    }
  }
  
  return { hasRTLAttack: attacks.length > 0, attacks };
};

/**
 * Variation Selector Steganography Detection
 * @internal - Currently unused, kept for potential future integration into security analysis
 */
export interface VariationSelectorAttack {
  hasVariationSelectors: boolean;
  clusters: {
    position: number;
    count: number;
    riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    description: string;
  }[];
  totalSelectors: number;
}

// Function kept for potential future integration into security analysis
// @ts-expect-error - Function intentionally kept unused for future integration
const _detectVariationSelectors = function detectVariationSelectors(_text: string): VariationSelectorAttack {
  const clusters: VariationSelectorAttack['clusters'] = [];
  const isVariationSelector = (code: number): boolean => 
    (code >= 0xFE00 && code <= 0xFE0F) || (code >= 0xE0100 && code <= 0xE01EF);
  
  let totalSelectors = 0;
  let currentCluster: { start: number; count: number } | null = null;
  
  for (let i = 0; i < _text.length; i++) {
    const code = _text.charCodeAt(i);
    
    if (isVariationSelector(code)) {
      totalSelectors++;
      if (currentCluster && currentCluster.start + currentCluster.count === i) {
        currentCluster.count++;
      } else {
        if (currentCluster && currentCluster.count >= 2) {
          clusters.push({
            position: currentCluster.start,
            count: currentCluster.count,
            riskLevel: currentCluster.count >= 8 ? 'CRITICAL' : currentCluster.count >= 4 ? 'HIGH' : 'MEDIUM',
            description: `${currentCluster.count} variation selectors - potential steganographic encoding`,
          });
        }
        currentCluster = { start: i, count: 1 };
      }
    } else {
      if (currentCluster && currentCluster.count >= 2) {
        clusters.push({
          position: currentCluster.start,
          count: currentCluster.count,
          riskLevel: currentCluster.count >= 8 ? 'CRITICAL' : currentCluster.count >= 4 ? 'HIGH' : 'MEDIUM',
          description: `${currentCluster.count} variation selectors - potential data hiding`,
        });
      }
      currentCluster = null;
    }
  }
  
  return { hasVariationSelectors: totalSelectors > 0, clusters, totalSelectors };
}
